/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.session.Session
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_error_delete
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_error_load

data class SessionDetailState(
    val session: Session? = null,
    val feeDisplay: String = "",
    val amountPaidDisplay: String = "",
    val dateDisplay: String = "",
    val isLoading: Boolean = true,
    val isDeleting: Boolean = false,
    val error: UiText? = null,
)

sealed interface SessionDetailEvent {
    data object SessionDeleted : SessionDetailEvent
}

sealed interface SessionDetailAction {
    data object DeleteClick : SessionDetailAction
}

class SessionDetailViewModel(
    private val sessionId: String,
    private val sessionRepository: SessionRepository,
    private val currencyFormatter: CurrencyFormatter,
    private val dateFormatter: DateFormatter,
) : BaseViewModel<SessionDetailState, SessionDetailEvent, SessionDetailAction>(SessionDetailState()) {

    init {
        loadSession()
    }

    override fun handleAction(action: SessionDetailAction) {
        when (action) {
            SessionDetailAction.DeleteClick -> deleteSession()
        }
    }

    private fun loadSession() {
        mutableStateFlow.value = state.copy(isLoading = true, error = null)
        viewModelScope.launch {
            sessionRepository.getSession(sessionId)
                .onSuccess { session ->
                    mutableStateFlow.value = state.copy(
                        session = session,
                        feeDisplay = currencyFormatter.format(session.fee),
                        amountPaidDisplay = currencyFormatter.format(session.amountPaid),
                        dateDisplay = dateFormatter.format(session.sessionDate) ?: session.sessionDate,
                        isLoading = false,
                    )
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        error = e.toUiText(Res.string.session_detail_error_load),
                    )
                }
        }
    }

    private fun deleteSession() {
        mutableStateFlow.value = state.copy(isDeleting = true, error = null)
        viewModelScope.launch {
            sessionRepository.deleteSession(sessionId)
                .onSuccess {
                    mutableStateFlow.value = state.copy(isDeleting = false)
                    sendEvent(SessionDetailEvent.SessionDeleted)
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isDeleting = false,
                        error = e.toUiText(Res.string.session_detail_error_delete),
                    )
                }
        }
    }
}
